/**
 * Idle Callback Hook - TypeScript
 * Portal ICAVI - Sprint 4 Migration
 * Hook para executar operações não críticas durante períodos de inatividade do navegador
 */
import { useCallback, useRef, useEffect } from 'react';
// ============================================================================
// Main Hook
// ============================================================================
/**
 * Hook para executar operações não críticas durante períodos de inatividade do navegador
 * Utiliza requestIdleCallback quando disponível, com fallback para setTimeout
 *
 * @returns Objeto com funções para agendar e cancelar callbacks idle
 *
 * @example
 * ```tsx
 * const { scheduleIdleCallback, cancelIdleCallback } = useIdleCallback();
 *
 * // Agendar operação em idle
 * const id = scheduleIdleCallback((deadline) => {
 *   if (deadline.timeRemaining() > 10) {
 *     // Executar operação não crítica
 *   }
 * });
 *
 * // Cancelar se necessário
 * cancelIdleCallback(id);
 * ```
 */
export const useIdleCallback = () => {
    const idleCallbacksRef = useRef(new Map());
    const timeoutFallbacksRef = useRef(new Map());
    const idCounterRef = useRef(0);
    /**
     * Cancela todos os callbacks pendentes
     */
    const cancelAllCallbacks = useCallback(() => {
        // Cancelar requestIdleCallback
        idleCallbacksRef.current.forEach(id => {
            if (typeof window !== 'undefined' && window.cancelIdleCallback) {
                window.cancelIdleCallback(id);
            }
        });
        idleCallbacksRef.current.clear();
        // Cancelar timeouts de fallback
        timeoutFallbacksRef.current.forEach(id => {
            clearTimeout(id);
        });
        timeoutFallbacksRef.current.clear();
    }, []);
    /**
     * Executa callback durante período de inatividade
     * @param callback Função a ser executada
     * @param options Opções de configuração
     * @returns ID do callback para cancelamento
     */
    const scheduleIdleCallback = useCallback((callback, options = {}) => {
        const { timeout = 5000, priority = 3 } = options;
        const callbackId = `idle_${++idCounterRef.current}`;
        // Wrapper para limpeza automática
        const wrappedCallback = (deadline) => {
            try {
                callback(deadline);
            }
            catch (error) {
                console.warn('Erro em idle callback:', error);
            }
            finally {
                // Limpar referências
                idleCallbacksRef.current.delete(callbackId);
                timeoutFallbacksRef.current.delete(callbackId);
            }
        };
        // Usar requestIdleCallback se disponível
        if (typeof window !== 'undefined' && window.requestIdleCallback) {
            const idleId = window.requestIdleCallback(wrappedCallback, {
                timeout,
            });
            idleCallbacksRef.current.set(callbackId, idleId);
        }
        else {
            // Fallback para setTimeout com delay baseado na prioridade
            const delay = Math.max(100, priority * 50);
            const timeoutId = setTimeout(() => {
                wrappedCallback({ timeRemaining: () => 50 }); // Mock deadline
            }, delay);
            timeoutFallbacksRef.current.set(callbackId, timeoutId);
        }
        return callbackId;
    }, []);
    /**
     * Cancela um callback específico
     * @param callbackId ID do callback a ser cancelado
     */
    const cancelIdleCallback = useCallback((callbackId) => {
        // Cancelar requestIdleCallback
        const idleId = idleCallbacksRef.current.get(callbackId);
        if (idleId && typeof window !== 'undefined' && window.cancelIdleCallback) {
            window.cancelIdleCallback(idleId);
            idleCallbacksRef.current.delete(callbackId);
        }
        // Cancelar timeout de fallback
        const timeoutId = timeoutFallbacksRef.current.get(callbackId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutFallbacksRef.current.delete(callbackId);
        }
    }, []);
    /**
     * Executa operação de cache durante período de inatividade
     * @param cacheOperation Operação de cache a ser executada
     * @param options Opções de configuração
     * @returns ID do callback para cancelamento
     */
    const scheduleIdleCacheOperation = useCallback((cacheOperation, options = {}) => {
        return scheduleIdleCallback(deadline => {
            // Verificar se ainda há tempo disponível
            if (deadline.timeRemaining() > 10) {
                cacheOperation();
            }
        }, { ...options, priority: 4 }); // Prioridade baixa para cache
    }, [scheduleIdleCallback]);
    /**
     * Executa limpeza de dados durante período de inatividade
     * @param cleanupOperation Operação de limpeza a ser executada
     * @param options Opções de configuração
     * @returns ID do callback para cancelamento
     */
    const scheduleIdleCleanup = useCallback((cleanupOperation, options = {}) => {
        return scheduleIdleCallback(deadline => {
            // Verificar se ainda há tempo disponível
            if (deadline.timeRemaining() > 5) {
                cleanupOperation();
            }
        }, { ...options, priority: 5 }); // Prioridade mais baixa para limpeza
    }, [scheduleIdleCallback]);
    /**
     * Executa múltiplas operações em lote durante período de inatividade
     * @param operations Array de operações a serem executadas
     * @param options Opções de configuração
     * @returns ID do callback para cancelamento
     */
    const scheduleBatchIdleOperations = useCallback((operations, options = {}) => {
        return scheduleIdleCallback(deadline => {
            let operationIndex = 0;
            // Executar operações enquanto houver tempo
            while (operationIndex < operations.length && deadline.timeRemaining() > 5) {
                const operation = operations[operationIndex];
                if (operation) {
                    try {
                        operation();
                    }
                    catch (error) {
                        console.warn(`Erro na operação ${operationIndex}:`, error);
                    }
                }
                operationIndex++;
            }
            // Se não conseguiu executar todas, agendar o restante
            if (operationIndex < operations.length) {
                const remainingOperations = operations.slice(operationIndex);
                scheduleBatchIdleOperations(remainingOperations, options);
            }
        }, options);
    }, [scheduleIdleCallback]);
    // Limpeza ao desmontar componente
    useEffect(() => {
        return () => {
            cancelAllCallbacks();
        };
    }, [cancelAllCallbacks]);
    return {
        scheduleIdleCallback,
        cancelIdleCallback,
        cancelAllCallbacks,
        scheduleIdleCacheOperation,
        scheduleIdleCleanup,
        scheduleBatchIdleOperations,
    };
};
// ============================================================================
// Specialized Hooks
// ============================================================================
/**
 * Hook simplificado para operações de cache em idle
 *
 * @returns Objeto com funções para agendar cache em idle
 *
 * @example
 * ```tsx
 * const { scheduleCache } = useIdleCache();
 *
 * // Agendar cache com delay de 1 segundo
 * scheduleCache(() => {
 *   localStorage.setItem('cache', data);
 * });
 * ```
 */
export const useIdleCache = () => {
    const { scheduleIdleCacheOperation, cancelIdleCallback } = useIdleCallback();
    /**
     * Agenda operação de cache para período de inatividade
     * @param operation Operação de cache
     * @param delay Delay antes de agendar (padrão: 1000ms)
     * @returns Timer ID do setTimeout
     */
    const scheduleCache = useCallback((operation, delay = 1000) => {
        return setTimeout(() => {
            scheduleIdleCacheOperation(operation);
        }, delay);
    }, [scheduleIdleCacheOperation]);
    return {
        scheduleCache,
        cancelIdleCallback,
    };
};
/**
 * Hook para limpeza automática em idle
 *
 * @returns Objeto com funções para agendar limpeza periódica
 *
 * @example
 * ```tsx
 * const { schedulePeriodicCleanup } = useIdleCleanup();
 *
 * // Agendar limpeza a cada 30 segundos
 * useEffect(() => {
 *   schedulePeriodicCleanup(() => {
 *     // Limpeza de cache expirado
 *   }, 30000);
 * }, []);
 * ```
 */
export const useIdleCleanup = () => {
    const { scheduleIdleCleanup, cancelIdleCallback } = useIdleCallback();
    /**
     * Agenda limpeza para período de inatividade
     * @param cleanupFn Função de limpeza
     * @param interval Intervalo entre limpezas em ms (padrão: 30000)
     */
    const schedulePeriodicCleanup = useCallback((cleanupFn, interval = 30000) => {
        const cleanup = () => {
            scheduleIdleCleanup(cleanupFn);
            // Agendar próxima limpeza
            setTimeout(cleanup, interval);
        };
        // Iniciar ciclo de limpeza
        setTimeout(cleanup, interval);
    }, [scheduleIdleCleanup]);
    return {
        schedulePeriodicCleanup,
        cancelIdleCallback,
    };
};
/**
 * Export default para compatibilidade
 */
export default useIdleCallback;
